import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { auth } from '../firebase-config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Mail, CheckCircle2 } from 'lucide-react';

// Validation schema
const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type EmailFormData = z.infer<typeof emailSchema>;

export default function MagicLinkForm() {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: searchParams.get('email') || '',
    },
  });

  // Pre-fill email from URL parameter
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setValue('email', emailParam);
    }
  }, [searchParams, setValue]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && !canResend) {
      setCanResend(true);
    }
  }, [resendTimer, canResend]);

  const onSubmit = async (data: EmailFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Generate and verify reCAPTCHA token (production only)
      const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

      if (recaptchaSiteKey && (window as any).grecaptcha?.enterprise) {
        try {
          const recaptchaToken = await (window as any).grecaptcha.enterprise.execute(
            recaptchaSiteKey,
            { action: 'LOGIN' }
          );

          // Verify reCAPTCHA token with backend
          const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
          const verifyUrl = `https://us-central1-${projectId}.cloudfunctions.net/verifyRecaptcha`;

          const verifyResponse = await fetch(verifyUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              token: recaptchaToken,
              expectedAction: 'LOGIN',
              siteKey: recaptchaSiteKey,
            }),
          });

          const verifyResult = await verifyResponse.json();

          if (!verifyResult.success) {
            setError('Security verification failed. Please try again.');
            setIsLoading(false);
            return;
          }
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          // Continue without reCAPTCHA if it fails (graceful degradation for dev)
          // In production, you might want to fail hard instead
        }
      }

      // Always redirect to app subdomain for authentication
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      const actionCodeSettings = {
        url: `${appUrl}/auth/verify`,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, data.email, actionCodeSettings);

      // Save email to localStorage for verification page
      window.localStorage.setItem('emailForSignIn', data.email);

      setIsSuccess(true);
      setCanResend(false);
      setResendTimer(60); // 1 minute cooldown
    } catch (err: any) {
      console.error('Error sending magic link:', err);
      setError(
        err.code === 'auth/invalid-email'
          ? 'Invalid email address'
          : err.code === 'auth/too-many-requests'
          ? 'Too many requests. Please try again later.'
          : 'Failed to send magic link. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center">
          <CheckCircle2 className="h-16 w-16 text-green-500" />
        </div>
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-semibold text-foreground">
            Check your email
          </h2>
          <p className="text-muted-foreground">
            We've sent a magic link to your email address.
            <br />
            Click the link to sign in.
          </p>
        </div>
        <div className="space-y-3">
          <p className="text-sm text-center text-muted-foreground">
            Didn't receive the email?
          </p>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              setIsSuccess(false);
              setError(null);
            }}
            disabled={!canResend}
          >
            {canResend ? (
              'Resend Magic Link'
            ) : (
              `Resend in ${resendTimer}s`
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-foreground">
          Email Address
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
            {...register('email')}
            disabled={isLoading}
            autoFocus
            autoComplete="email"
          />
        </div>
        {errors.email && (
          <p className="text-sm text-destructive" role="alert">
            {errors.email.message}
          </p>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading}
        size="lg"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending Magic Link...
          </>
        ) : (
          'Send Magic Link'
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        By continuing, you agree to our Terms of Service and Privacy Policy
      </p>
    </form>
  );
}
